import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { saveDevice, deleteDevice } from "../../store/devicesSlice";
import { setSelectedDevice } from "../../store/uiSlice";
import { setCenter } from "../../store/uiSlice";
import { selectAllUserMaps, selectCurrentMap } from "../../store/mapsSlice";
import { IDevice } from "../../types";
import DevicePickerModal from "./DevicePickerModal";
import DeviceModal from "./DeviceModal";
import SignalStrengthIndicator from "./SignalStrengthIndicator";

const DeviceManager: React.FC = () => {
  // console.log("🔵 DeviceManager rendering");
  const dispatch = useDispatch<AppDispatch>();
  const { language, selectedDeviceMac } = useSelector(
    (state: RootState) => state.ui
  );
  // Only subscribe to the device data we actually use in this component
  const configuredDevices = useSelector(
    (state: RootState) => state.devices.configured
  );
  const liveDevices = useSelector((state: RootState) => state.devices.entities);
  const devicesLoading = useSelector(
    (state: RootState) => state.devices.loading
  );

  const { entities: gateways } = useSelector(
    (state: RootState) => state.gateways
  );
  const maps = useSelector(selectAllUserMaps);
  const currentMap = useSelector(selectCurrentMap);
  // console.log("DeviceManager currentMap:", currentMap);
  // Persist picker visibility across remounts (React Fast Refresh/StrictMode) so it doesn't auto-close
  const [showDevicePicker, setShowDevicePicker] = useState<boolean>(
    () => (window as any).__showDevicePicker || false
  );
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDeviceForModal, setSelectedDeviceForModal] =
    useState<IDevice | null>(null);
  const [editingDevice, setEditingDevice] = useState<IDevice | null>(null);
  const [showOnlyCurrentMap, setShowOnlyCurrentMap] = useState(false);

  const isMountedRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // Keep a global flag so if this component remounts, the modal state is restored
  useEffect(() => {
    (window as any).__showDevicePicker = showDevicePicker;
  }, [showDevicePicker]);

  // Debug: Log when showDevicePicker changes
  useEffect(() => {
    // console.log("🔵 showDevicePicker state changed to:", showDevicePicker);
  }, [showDevicePicker]);

  // Load checkbox state from localStorage on mount (only once)
  useEffect(() => {
    if (hasLoadedRef.current) return; // Prevent double loading in Strict Mode

    try {
      const savedState = localStorage.getItem(
        "deviceManager_showOnlyCurrentMap"
      );
      if (savedState !== null) {
        const parsedState = JSON.parse(savedState);
        // console.log("Loading device filter state:", parsedState);
        setShowOnlyCurrentMap(parsedState);
      } else {
        // console.log("No saved device filter state found");
      }
    } catch (error) {
      console.error("Error loading device filter state:", error);
    }

    hasLoadedRef.current = true;
    isMountedRef.current = true;
  }, []);

  // Save checkbox state to localStorage when it changes
  useEffect(() => {
    if (isMountedRef.current && hasLoadedRef.current) {
      try {
        // console.log("Saving device filter state:", showOnlyCurrentMap);
        localStorage.setItem(
          "deviceManager_showOnlyCurrentMap",
          JSON.stringify(showOnlyCurrentMap)
        );
        // console.log("Successfully saved device filter state");
      } catch (error) {
        console.error("Error saving device filter state:", error);
      }
    }
  }, [showOnlyCurrentMap]);

  const handleDeviceAdded = useCallback(() => {
    // console.log("🔵 handleDeviceAdded called");
    setShowDevicePicker(false);
  }, []);

  const handleCloseDevicePicker = useCallback(() => {
    // console.log("🔵 handleCloseDevicePicker called");
    setShowDevicePicker(false);
  }, []);

  const handleEditDevice = async (device: IDevice) => {
    if (editingDevice && editingDevice.name) {
      try {
        await dispatch(
          saveDevice({
            ...device,
            name: editingDevice.name,
          })
        );
        setEditingDevice(null);
      } catch (error) {
        console.error("Failed to update device:", error);
      }
    }
  };

  const handleDeleteDevice = async (mac: string) => {
    if (
      window.confirm(
        language === "he"
          ? "האם אתה בטוח שברצונך למחוק התקן זה?"
          : "Are you sure you want to delete this device?"
      )
    ) {
      try {
        await dispatch(deleteDevice(mac));
      } catch (error) {
        console.error("Failed to delete device:", error);
      }
    }
  };

  const getDeviceStatus = (mac: string) => {
    const liveDevice = liveDevices[mac];
    if (!liveDevice || !liveDevice.lastSeen) {
      return {
        status: "offline",
        color: "text-red-500",
        label: language === "he" ? "לא מחובר" : "Offline",
      };
    }

    // if (
    //   !(!liveDevice || !liveDevice.lastSeen) &&
    //   liveDevice.mac === "FC141679E2B4"
    // ) {
    // console.log("FC141679E2B4", liveDevice);
    // console.log("lastSeen", liveDevice.lastSeen);
    // console.log("lastSignalData", liveDevice.lastSignalData);
    // }

    const lastSeen = new Date(liveDevice.lastSeen);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastSeen.getTime()) / (1000 * 60);

    if (diffMinutes < 5) {
      return {
        status: "online",
        color: "text-green-500",
        label: language === "he" ? "מחובר" : "Online",
      };
    } else if (diffMinutes < 10) {
      return {
        status: "warning",
        color: "text-yellow-500",
        label: language === "he" ? "חיבור חלש" : "Warning",
      };
    } else {
      return {
        status: "offline",
        color: "text-red-500",
        label: language === "he" ? "לא מחובר" : "Offline",
      };
    }
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return language === "he" ? "לא זמין" : "N/A";
    const date = new Date(lastSeen);
    return date.toLocaleTimeString();
  };

  const getDeviceLocation = (mac: string) => {
    const liveDevice = liveDevices[mac];
    if (!liveDevice?.strongestGateway) {
      return {
        gatewayName: language === "he" ? "לא זמין" : "N/A",
        mapName: language === "he" ? "לא זמין" : "N/A",
        fullLocation:
          language === "he" ? "מיקום לא זמין" : "Location not available",
      };
    }

    const gateway = gateways[liveDevice.strongestGateway];
    if (!gateway) {
      return {
        gatewayName: language === "he" ? "שער לא נמצא" : "Gateway not found",
        mapName: language === "he" ? "לא זמין" : "N/A",
        fullLocation: language === "he" ? "שער לא נמצא" : "Gateway not found",
      };
    }

    const gatewayName = gateway.name || gateway.mac;

    if (!gateway.mapId) {
      return {
        gatewayName,
        mapName: language === "he" ? "מפה לא מוגדרת" : "No map assigned",
        fullLocation: gatewayName,
      };
    }

    // Handle both populated object and string mapId
    const mapId =
      typeof gateway.mapId === "object" &&
      gateway.mapId &&
      "_id" in gateway.mapId
        ? (gateway.mapId as any)._id
        : gateway.mapId;
    const map = maps.find((m) => m._id === mapId);
    const mapName =
      map?.name || (language === "he" ? "מפה לא נמצאת" : "Map not found");

    return {
      gatewayName,
      mapName,
      fullLocation: `${gatewayName} (${mapName})`,
    };
  };

  const handleSelectDevice = (device: IDevice) => {
    const liveDevice = liveDevices[device.mac];

    // Toggle selection - if already selected, deselect
    if (selectedDeviceMac === device.mac) {
      dispatch(setSelectedDevice(null));
    } else {
      // Select the device
      dispatch(setSelectedDevice(device.mac));

      // Center map on device if it has a position
      if (liveDevice?.position) {
        dispatch(setCenter([liveDevice.position.y, liveDevice.position.x]));
      }
    }
  };

  // Show only configured live devices with consistent name resolution
  const devices = Object.values(liveDevices || {})
    .filter((liveDevice) => {
      // Only show devices that are configured by the user
      return configuredDevices?.[liveDevice.mac];
    })
    .filter((liveDevice) => {
      // Filter by current map if checkbox is checked
      if (!showOnlyCurrentMap || !currentMap) {
        return true;
      }

      // Check if device's strongest gateway is on the current map
      if (!liveDevice.strongestGateway) {
        return false;
      }

      const gateway = gateways[liveDevice.strongestGateway];
      return gateway && gateway.mapId === currentMap._id;
    })
    .map((liveDevice) => {
      const configuredDevice = configuredDevices?.[liveDevice.mac];

      // Consistent name resolution: prioritize displayName, then API name, then MAC
      const deviceName =
        liveDevice.customSettings?.displayName ||
        configuredDevice?.name ||
        liveDevice.name ||
        liveDevice.mac;

      return {
        ...liveDevice,
        name: deviceName,
        isConfigured: !!configuredDevice,
      };
    });

  React.useEffect(() => {
    console.info("[DeviceManager] live device keys", Object.keys(liveDevices || {}));
  }, [liveDevices]);

  React.useEffect(() => {
    console.info("[DeviceManager] configured device keys", Object.keys(configuredDevices || {}));
  }, [configuredDevices]);

  React.useEffect(() => {
    console.info("[DeviceManager] rendered devices", {
      keys: devices.map((d) => d.mac),
      count: devices.length,
      showOnlyCurrentMap,
      currentMapId: currentMap?._id,
    });
  }, [devices, showOnlyCurrentMap, currentMap]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {language === "he" ? "ניהול התקנים" : "Device Management"}
          </h3>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <label className="flex items-center space-x-2 rtl:space-x-reverse text-sm">
              <input
                type="checkbox"
                checked={showOnlyCurrentMap}
                onChange={(e) => {
                  // console.log("Checkbox onChange triggered:", e.target.checked);
                  setShowOnlyCurrentMap(e.target.checked);
                }}
                disabled={!currentMap}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={currentMap ? "text-gray-700" : "text-gray-400"}>
                {language === "he"
                  ? "הצג רק מפה נוכחית"
                  : "Show only current map"}
              </span>
            </label>
            <button
              onClick={() => {
                // console.log("🔵 Add Device button clicked");
                // console.log(
                //   "🔵 Current showDevicePicker state:",
                //   showDevicePicker
                // );
                setShowDevicePicker(true);
                // console.log("🔵 setShowDevicePicker(true) called");
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "הוסף התקן" : "Add Device"}
            </button>
          </div>
        </div>

        {/* Device Count */}
        <p className="text-sm text-gray-600 mt-1">
          {language === "he"
            ? `${devices.length} התקנים זמינים`
            : `${devices.length} devices available`}
        </p>
      </div>

      {/* Device Picker Modal */}
      {showDevicePicker && (
        <DevicePickerModal
          isOpen={showDevicePicker}
          onClose={handleCloseDevicePicker}
          onDeviceAdded={handleDeviceAdded}
        />
      )}

      {/* Device Modal */}
      {showDeviceModal && selectedDeviceForModal && (
        <DeviceModal
          isOpen={showDeviceModal}
          onClose={() => {
            setShowDeviceModal(false);
            setSelectedDeviceForModal(null);
          }}
          device={selectedDeviceForModal}
        />
      )}

      {/* Device List */}
      <div className="flex-1">
        {devicesLoading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">
              {language === "he" ? "טוען..." : "Loading..."}
            </p>
          </div>
        ) : devices.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <p className="mt-2">
              {language === "he"
                ? showOnlyCurrentMap && currentMap
                  ? "אין התקנים במפה הנוכחית"
                  : "אין התקנים מוגדרים"
                : showOnlyCurrentMap && currentMap
                ? "No devices on current map"
                : "No devices configured"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {devices.map((device) => {
              const status = getDeviceStatus(device.mac);
              const liveDevice = liveDevices[device.mac];
              const isEditing = editingDevice?.mac === device.mac;
              const isSelected = selectedDeviceMac === device.mac;
              const location = getDeviceLocation(device.mac);
              console.info("[DeviceManager] row render", {
                mac: device.mac,
                hasSignal: !!(liveDevice?.signal || liveDevice?.lastSignalData),
                strongestGateway: liveDevice?.strongestGateway,
                lastSeen: liveDevice?.lastSeen,
                position: !!liveDevice?.position,
              });

              return (
                <div
                  key={device.mac}
                  className={`p-4 cursor-pointer transition-colors duration-200 ${
                    isSelected
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelectDevice(device)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingDevice?.name || ""}
                          onChange={(e) =>
                            setEditingDevice({
                              ...device,
                              name: e.target.value,
                            })
                          }
                          className="font-medium text-gray-900 border-b border-blue-500 focus:outline-none bg-transparent"
                          onBlur={() => handleEditDevice(device)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleEditDevice(device)
                          }
                          autoFocus
                        />
                      ) : (
                        <h4 className="font-medium text-gray-900">
                          {device.name}
                        </h4>
                      )}
                      <p className="text-sm text-gray-600">{device.mac}</p>
                      <div className="flex items-center space-x-4 rtl:space-x-reverse mt-1">
                        <span className={`text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        <SignalStrengthIndicator
                          mac={device.mac}
                          signals={
                            liveDevice?.signal || liveDevice?.lastSignalData
                          }
                          className="text-xs"
                        />
                        <span className="text-xs text-gray-500">
                          {language === "he" ? "נראה לאחרונה:" : "Last seen:"}{" "}
                          {formatLastSeen(liveDevice?.lastSeen)}
                        </span>
                        <div className="text-xs text-blue-600 mt-1">
                          <span className="font-medium">
                            {language === "he"
                              ? "מיקום נוכחי:"
                              : "Current location:"}{" "}
                          </span>
                          <span>{location.fullLocation}</span>
                        </div>
                      </div>
                      {/* {liveDevice?.position && (
                        <div className="text-xs text-gray-500 mt-1">
                          {language === "he" ? "קואורדינטות:" : "Coordinates:"}{" "}
                          ({liveDevice.position.x.toFixed(0)},{" "}
                          {liveDevice.position.y.toFixed(0)})
                        </div>
                      )} */}
                    </div>
                    <div className="flex flex-col items-center justify-between space-y-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDeviceForModal(device);
                          setShowDeviceModal(true);
                        }}
                        className="text-blue-500 hover:text-blue-700 p-1 transition-colors"
                        title={
                          language === "he" ? "פרטי התקן" : "Device Details"
                        }
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDevice(device.mac);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title={language === "he" ? "מחק התקן" : "Delete device"}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceManager;
